import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Activity, Calendar, Clock, Users, Zap, BarChart3, Cpu } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCompactNumber } from '../../utils/formatNumber';
import PageTitle from '../../components/PageTitle';

interface FallbackStats {
  summary: {
    totalAll: number;
    tokensAll: number;
    today: number;
    last7Days: number;
    last30Days: number;
    rangeCount: number;
    rangeTokens: number;
    rangeUniqueUsers: number;
  };
  trend: Array<{ label: string; count: number; tokens: number }>;
  byModel: Array<{ model: string; count: number; tokens: number }>;
  topUsers: Array<{ email: string; count: number; tokens: number }>;
}

type Range = '7d' | '30d' | '90d';

function DashboardFallback() {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState<FallbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>('30d');

  const loadStats = useCallback(async (selected: Range) => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/fallback-stats', { params: { range: selected } });
      setData(res.data);
    } catch (error) {
      console.error('Failed to load fallback stats:', error);
      toast.error(t('fallback.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStats(range);
  }, [range, loadStats]);

  const maxTrend = data ? Math.max(1, ...data.trend.map(d => d.count)) : 1;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="fallback.title" namespace="dashboard" descriptionKey="fallback.subtitle" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('fallback.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('fallback.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {(['7d', '30d', '90d'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                range === r ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {r === '7d' ? t('fallback.days_7') : r === '30d' ? t('fallback.days_30') : t('fallback.days_90')}
            </button>
          ))}
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Shield} label={t('fallback.total_all')} value={formatCompactNumber(summary?.totalAll || 0)} accent="text-primary bg-primary/10" />
        <MetricCard icon={Clock} label={t('fallback.today')} value={formatCompactNumber(summary?.today || 0)} accent="text-purple-500 bg-purple-500/10" />
        <MetricCard icon={Calendar} label={t('fallback.last_7d')} value={formatCompactNumber(summary?.last7Days || 0)} accent="text-emerald-500 bg-emerald-500/10" />
        <MetricCard icon={Activity} label={t('fallback.last_30d')} value={formatCompactNumber(summary?.last30Days || 0)} accent="text-amber-500 bg-amber-500/10" />
        <MetricCard icon={Activity} label={t('fallback.range_count')} value={formatCompactNumber(summary?.rangeCount || 0)} accent="text-blue-500 bg-blue-500/10" />
        <MetricCard icon={Users} label={t('fallback.range_users')} value={formatCompactNumber(summary?.rangeUniqueUsers || 0)} accent="text-violet-500 bg-violet-500/10" />
        <MetricCard icon={Zap} label={t('fallback.range_tokens')} value={formatCompactNumber(summary?.rangeTokens || 0)} accent="text-rose-500 bg-rose-500/10" />
        <MetricCard icon={Zap} label={t('fallback.tokens_all')} value={formatCompactNumber(summary?.tokensAll || 0)} accent="text-teal-500 bg-teal-500/10" />
      </div>

      {/* Daily trend */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
        <div className="flex items-center gap-2.5 mb-4">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">{t('fallback.trend_title')}</h3>
        </div>
        {data && data.trend.length > 0 ? (
          <div className="flex items-end gap-1 h-40">
            {data.trend.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 group min-w-0">
                <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">{d.count}</span>
                <div
                  className="w-full bg-primary/70 hover:bg-primary rounded-t transition-colors"
                  style={{ height: `${(d.count / maxTrend) * 100}%`, minHeight: d.count > 0 ? '2px' : '0' }}
                  title={`${d.label}: ${d.count}`}
                />
                {(i % Math.ceil(data.trend.length / 12) === 0) && (
                  <span className="text-[9px] text-muted-foreground whitespace-nowrap">{d.label}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-[13px] text-muted-foreground">{t('fallback.no_data')}</div>
        )}
      </div>

      {/* Breakdown tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5">
            <Cpu className="h-4 w-4 text-primary" />
            <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">{t('fallback.by_model')}</h3>
          </div>
          {data && data.byModel.length > 0 ? (
            <div className="divide-y divide-border">
              <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                <span>{t('fallback.col_model')}</span>
                <span className="text-right w-20">{t('fallback.col_requests')}</span>
                <span className="text-right w-20">{t('fallback.col_tokens')}</span>
              </div>
              {data.byModel.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-2.5 text-[13px]">
                  <span className="font-mono text-foreground truncate" title={m.model}>{m.model}</span>
                  <span className="text-right w-20 tabular-nums text-foreground">{formatCompactNumber(m.count)}</span>
                  <span className="text-right w-20 tabular-nums text-muted-foreground">{formatCompactNumber(m.tokens)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-[13px] text-muted-foreground">{t('fallback.no_data')}</div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5">
            <Users className="h-4 w-4 text-primary" />
            <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">{t('fallback.top_users')}</h3>
          </div>
          {data && data.topUsers.length > 0 ? (
            <div className="divide-y divide-border">
              <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                <span>{t('fallback.col_user')}</span>
                <span className="text-right w-20">{t('fallback.col_requests')}</span>
                <span className="text-right w-20">{t('fallback.col_tokens')}</span>
              </div>
              {data.topUsers.map((u, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-2.5 text-[13px]">
                  <span className="text-foreground truncate" title={u.email}>{u.email}</span>
                  <span className="text-right w-20 tabular-nums text-foreground">{formatCompactNumber(u.count)}</span>
                  <span className="text-right w-20 tabular-nums text-muted-foreground">{formatCompactNumber(u.tokens)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-[13px] text-muted-foreground">{t('fallback.no_data')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }: { icon: any; label: string; value: string; accent: string }) {
  return (
    <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-lg font-bold text-foreground tabular-nums truncate">{value}</div>
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider truncate">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default DashboardFallback;
